import { ClassNameMap, Tooltip, Typography } from '@mui/material';
import {
    DataGridPremium,
    GRID_AGGREGATION_FUNCTIONS,
    GridCellParams,
    GridColDef,
    GridEditModes,
    GridPaginationModel,
    GridPinnedRowsProp,
    GridRenderCellParams,
    GridRowEditStartParams,
    GridRowEditStopParams,
    GridRowId,
    GridRowModel,
    GridValidRowModel,
    MuiEvent,
    ToolbarPropsOverrides,
    useGridApiRef,
    useKeepGroupedColumnsHidden
} from '@mui/x-data-grid-premium';
import { find } from 'lodash';
import type { KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import React, { Fragment, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AllPermissions, getRowPermissions } from '../auth/api';
import { SECURITY_ACTIONS, useEntitySecurityEvaluator, type FeatureName } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import Confirm from '../components/Confirm';
import { DATA_GRID_CONSTANTS } from '../constants/constants';
import { DATA_GRID_EVENTS, ROW_FOCUS_OUT_EDIT_ACTIONS } from '../constants/dataGridConstants';
import { useOnMobile } from '../hooks/breakpoints';
import { useShallowMergeSxProps } from '../hooks/useShallowMergeSxProps';
import I18n, { useI18n } from '../i18n/I18n';
import { stripFavouriteFields } from './columnVisibilityFavouriteUtils';
import CustomLoadingOverlay from './CustomLoadingOverlay';
import { CustomToolbar } from './CustomToolbar';
import { CellContainer } from './DataGrid/CellContainer';
import { DataGridActionsCell } from './DataGrid/DataGridActionCell';
import { DataGridActionsContext } from './DataGrid/DataGridActionsContext';
import {
    getGroupingLabelValue,
    getOriginalRowForEdit,
    getRowFocusOutEditAction,
    hasEditableRowChanges,
    isMissingGroupingLabel,
    transformDataGridCustomActions
} from './DataGrid/dataGridUtils';
import { EMPTY_GRID_GROUPING_MODEL, useGridSettings } from './DataGrid/gridSettings';
import { withFlatSortComparator } from './DataGrid/gridSettings/ui/utils';
import { DataGridActionsContextType, DataGridProps, RowAction, Toolbar, ToolbarOptions } from './DataGrid/types';
import {
    AUTOSIZE_OPTIONS,
    AUTOSIZE_PENDING_CLASSNAME,
    AUTOSIZED_DATA_GRID_SX,
    columnHasAutosizeContent,
    prepareColumnForAutosize,
    useAutosizeColumns
} from './DataGrid/useAutosizeColumns';
import { useDataGridColumnViewFeature } from './DataGrid/useDataGridColumnViewFeature';
import { applyManualColumnWidths, usePreserveColumnConfiguration } from './DataGrid/usePreserveColumnConfiguration';
import { RenderDataGridCellValue } from './RenderDataGridCellValue';
import { isRichTextColumn } from './richText/richTextColumn';
import RichTextDisplayCell from './richText/RichTextDisplayCell';
import RichTextEditCell from './richText/RichTextEditCell';
import { useCreateDefaultsFromViewContext } from './useCreateDefaultsFromViewContext';
import { DATA_GRID_LIST_SX, useDataGridStyles } from './useDataGridStyles';

const EXPERIMENTAL_FEATURES = { warnIfFocusStateIsNotSynced: true };
const FULL_SIZE_TOOLTIP_ANCHOR_STYLE = { display: 'block', width: '100%', height: '100%' } as const;

const ROW_TYPES_WITH_ACTIONS_MENU: GridCellParams['rowNode']['type'][] = ['leaf', 'pinnedRow'] as const;

const flushPendingRowEditMutations = (api: object | null, rowId: GridRowId): void => {
    if (!api) {
        return;
    }

    const pendingMutationRunner = Reflect.get(api, 'runPendingEditCellValueMutation');
    if (typeof pendingMutationRunner !== 'function') {
        return;
    }

    pendingMutationRunner.call(api, rowId);
};

const hasTooltipContent = (tooltip: ReactNode): boolean => {
    if (typeof tooltip === 'string') {
        return tooltip.trim().length > 0;
    }

    if (tooltip === undefined || tooltip === null || typeof tooltip === 'boolean') {
        return false;
    }

    if (Array.isArray(tooltip)) {
        return tooltip.some(hasTooltipContent);
    }

    if (isValidElement<{ children?: ReactNode }>(tooltip) && tooltip.type === Fragment) {
        return hasTooltipContent(tooltip.props.children);
    }

    return true;
};

export default function DataGrid({
    id,
    feature,
    entityName = undefined,
    rows,
    columns,
    rowCount = 0,
    disablePagination = false,
    hideFooterRowCount = false,
    serverPagination = true,
    paginationModel = undefined,
    setRows = undefined,
    includeActions = true,
    addTitle = undefined,
    onAdd = undefined,
    onEdit = undefined,
    onShare = undefined,
    onCopy = undefined,
    onDelete = undefined,
    onDetails = undefined,
    sortingMode = 'server',
    toolbar = {} as Toolbar,
    toolbarOptions = {} as ToolbarOptions,
    toolbarSx = undefined,
    onColumnOrderChange = undefined,
    disableColumnResize = false,
    disableColumnSorting = false,
    enableInlineEditing = false,
    hideEditActionWhenInlineEditing = false,
    errors = undefined,
    asIsColumns = [],
    page = DATA_GRID_CONSTANTS.defaultPageNumber,
    sortModel = undefined,
    filterModel = undefined,
    onSortModelChange = () => {},
    onPageChange = undefined,
    pageSize = DATA_GRID_CONSTANTS.defaultPageSize,
    onPageSizeChange = undefined,
    clickableColumn = undefined,
    loading = false,
    getRowClassName = undefined,
    onDeleteHandle = undefined,
    defaultColumnVisibility = {},
    columnVisibilityPresets = undefined,
    onActivateOrInactivateHandle = undefined,
    customActions = [],
    customRowActions = [],
    containerSx = undefined,
    onCreateNewRow = undefined,
    isRowValid = undefined,
    append = false,
    rowReordering = false,
    onRowOrderChange = undefined,
    hideFooterSelectedRowCount = false,
    hideHeader = false,
    hideFooter = false,
    autoHeight = false,
    disableVirtualization = false,
    getDetailPanelHeight = () => 'auto',
    getDetailPanelContent = undefined,
    slots = undefined,
    slotProps = undefined,
    disableColumnMenu = false,
    disableColumnFilter = false,
    density = 'standard',
    rowGroupingModel,
    onRowGroupingModelChange,
    isCellClickable = undefined,
    isCellEditable = undefined,
    listViewConfiguration = {
        enableListView: false,
        listViewColDef: { field: 'listColumn', renderCell: () => <></> }
    },
    processRowUpdate = undefined,
    enableInlineDelete = false,
    enableRichTextEditing = false,

    // Deprecated bellow:
    handleRowAction = undefined,
    disableRowSelectionOnClick = true,
    checkboxSelection = false,
    isRowSelectable = undefined,
    rowSelectionModel = undefined,
    onRowSelectionModelChange = undefined,
    newRowOnTop = true,
    forceDefaultColumnVisibility = false,
    inActivateOnBlur = false,
    commitRowOnBlur = false,
    commitRowOnBlurOnlyWhenDirty = false,
    getIsRowCommitInProgress,
    getShouldSuppressRowCommitOnBlur,
    exitEditModeBeforeProcessRowUpdate = false,
    disableAdd = false,
    triggerEditMode = false,
    onDynamicRowAdd = undefined,
    detailPanelExpandedRowIds = undefined,
    handleDetailPanelExpandedRowIdsChange = undefined,
    aggregationModel = undefined,
    copyTooltipTitle = '',
    defaultGroupingExpansionDepth = 0,
    isGroupedColumnsHidden = false,
    defaultSelectedPreset = undefined,
    enabledFavouritePreset = false,
    groupForPreset = [],
    defaultSelectedPresetLabel = undefined,
    customAggregationFunctions = undefined,
    hideMyColumnsPreset = false,
    hideAllColumnsPreset = false,
    hideDefaultColumnsPreset = false,
    noWrapColValue = true,
    externalColumnVisibilityModel = undefined, // controlled
    externalOnColumnVisibilityModelChange = undefined, // controlled
    onRowEditStart = undefined,
    onRowEditStop = undefined,
    scrollEndThreshold,
    onScrollEnd,
    externalAddTriggerValue,
    groupingColDef,
    createDefaultsFromViewContext = undefined,
    shouldScrollRight = false,
    autoSizeColumns = false,
    preserveConfiguration = false,
    preserveAutosize = false,
    preserveColumnWidths = false,
    enableSaveColumnView = false,
    enableColumnOrder = false,
    enableGridSettings = false,
    gridSettings = undefined,
    settingsEntityName = undefined
}: DataGridProps) {
    // ============================================================================
    // CONTEXT & HOOKS
    // ============================================================================
    const { getPreference, savePreference, preferencesLoaded } = useContext(UserContext);
    const apiRef = useGridApiRef();

    const classes: ClassNameMap = useDataGridStyles();
    const listViewSx = useShallowMergeSxProps(DATA_GRID_LIST_SX, containerSx);
    const baseGridSx = listViewConfiguration.enableListView ? listViewSx : (containerSx ?? {});
    const isMobile = useOnMobile();
    const evaluateEntitySecurity = useEntitySecurityEvaluator(feature as FeatureName);
    const canCreateRow = useMemo(() => {
        const { allowed, pending } = evaluateEntitySecurity(SECURITY_ACTIONS.EDIT, {});
        return Boolean(allowed && !pending);
    }, [evaluateEntitySecurity]);
    const t = useI18n();
    const missingLabel = t('common.missing');

    const actionLabels = useMemo(() => {
        const saveLabel = t('dialog.save');
        const cancelLabel = t('dialog.cancel');
        const editLabel = t('dialog.edit');
        const shareLabel = t('share');
        const copyLabel = t('dialog.copy');
        const deleteLabel = t('dialog.delete');

        return { saveLabel, cancelLabel, editLabel, shareLabel, copyLabel, deleteLabel };
    }, [t]);

    // ============================================================================
    // STATE
    // ============================================================================
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [activeRow, setActiveRow] = useState<GridRowModel>();
    const [rowPendingDeletion, setRowPendingDeletion] = useState<GridRowModel>();
    const [pinnedRows, setPinnedRows] = useState<GridPinnedRowsProp<GridValidRowModel>>(undefined);
    const isFirstPresetChange = useRef(true);
    const [rowGroupingModelState, setRowGroupingModelState] = useState<string[]>(rowGroupingModel || []);
    const externalAddTriggerValueHandledRef = useRef<number>(0);
    const rowsRef = useRef<GridRowModel[]>(rows);
    const processRowUpdateCallbackRef = useRef<
        ((update: GridRowModel, original: GridRowModel) => Promise<GridRowModel>) | undefined
    >(undefined);

    // ============================================================================
    // COMPUTED VALUES
    // ============================================================================
    const editMode = useMemo(() => (enableInlineEditing ? GridEditModes.Row : undefined), [enableInlineEditing]);

    const showToolbar = useMemo(
        () => Object.keys(toolbar).length > 0 || preserveAutosize || preserveConfiguration || enableGridSettings,
        [enableGridSettings, preserveAutosize, preserveConfiguration, toolbar]
    );

    const columnHeaderHeight = useMemo(() => (hideHeader ? 0 : 56), [hideHeader]);

    const paginationMode = useMemo(
        () => (serverPagination ? 'server' : 'client') as 'server' | 'client',
        [serverPagination]
    );

    const detailPanelExpandedRowIdsSet = useMemo(() => new Set(detailPanelExpandedRowIds), [detailPanelExpandedRowIds]);

    // ============================================================================
    // EFFECTS
    // ============================================================================
    useEffect(() => {
        if (triggerEditMode && apiRef.current) {
            onDynamicRowAdd(apiRef);
        }
    }, [apiRef, onDynamicRowAdd, triggerEditMode]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        if (enableGridSettings || !rowGroupingModel) {
            return;
        }

        if (
            rowGroupingModelState.length === rowGroupingModel.length &&
            rowGroupingModelState.every((field, index) => field === rowGroupingModel[index])
        ) {
            return;
        }

        setRowGroupingModelState(rowGroupingModel);
    }, [enableGridSettings, rowGroupingModel, rowGroupingModelState]);

    useEffect(() => {
        apiRef.current.subscribeEvent('cellFocusIn', (params) => {
            setTimeout(() => {
                const div = document.querySelector(
                    `div[data-id="${params.id}"] > div[data-field="${params.field}"] input`
                ) as HTMLElement | null;
                if (div) {
                    div.focus();
                }
            }, 200);
        });
    }, [apiRef]);

    /**
     * Scroll the grid to the left or right based on the shouldScrollRight flag.
     */
    useEffect(() => {
        if (!shouldScrollRight) {
            return;
        }

        requestAnimationFrame(() => {
            // Scope the lookup to this grid instance to avoid picking another grid's scroller
            const rootElement = apiRef.current.rootElementRef?.current;
            const scrollContainer = rootElement?.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
            if (scrollContainer) {
                // Scroll to the rightmost position
                scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
            }
        });
    }, [apiRef, shouldScrollRight]);

    const persistAutosize = preserveAutosize || preserveConfiguration;
    const persistColumnWidths = preserveColumnWidths || preserveConfiguration;
    const canSaveColumnView = enableSaveColumnView || enableColumnOrder || enableGridSettings;
    const { autosizeEnabled, manualWidths, setAutosizeEnabled } = usePreserveColumnConfiguration({
        apiRef,
        persistAutosize,
        persistColumnWidths,
        gridId: id,
        preferencesLoaded,
        getPreference,
        savePreference
    });
    const {
        sortModel: gridSettingsSortModel,
        rowGroupingModel: gridSettingsRowGroupingModel,
        groupingSortComparator: gridSettingsGroupingSortComparator,
        flatSortComparator: gridSettingsFlatSortComparator,
        flatSortField: gridSettingsFlatSortField,
        onSortModelChange: onGridSettingsSortModelChange,
        getCurrentGridSettings,
        defaultPreference: defaultGridSettings,
        applyGridSettings,
        gridSettingsRevision,
        toolbarControl: gridSettingsControl,
        modal: gridSettingsModal
    } = useGridSettings({
        enabled: enableGridSettings,
        showAutosize: persistAutosize,
        settingsEntityName,
        rows,
        config: gridSettings,
        defaultSortModel: sortModel,
        defaultRowGroupingModel: rowGroupingModel,
        onSortModelChange,
        autosizeEnabled,
        onAutosizeEnabledChange: setAutosizeEnabled
    });
    const appliedGroupingFieldCountRef = useRef(0);
    useEffect(() => {
        const nextGroupingFieldCount = gridSettingsRowGroupingModel?.length ?? 0;
        if (!enableGridSettings || gridSettingsRevision === 0) {
            appliedGroupingFieldCountRef.current = nextGroupingFieldCount;
            return;
        }

        // Leaving an ungrouped view rebuilds groups with the previous tree's collapsed
        // state, so default views come back hidden until a manual expand.
        const restoredGrouping = appliedGroupingFieldCountRef.current === 0 && nextGroupingFieldCount > 0;
        appliedGroupingFieldCountRef.current = nextGroupingFieldCount;
        apiRef.current.applySorting();
        if (defaultGroupingExpansionDepth !== -1 || !restoredGrouping) {
            return;
        }

        const frame = requestAnimationFrame(function expandRestoredGroups() {
            apiRef.current?.expandAllRows();
            apiRef.current?.applySorting();
        });
        return function cancelExpandRestoredGroups() {
            cancelAnimationFrame(frame);
        };
    }, [apiRef, defaultGroupingExpansionDepth, enableGridSettings, gridSettingsRevision, gridSettingsRowGroupingModel]);

    // Autosize layout (flex stripped, header filler) stays on in manual mode so switching
    // modes does not shift headers against body cells.
    const finalCustomSx = useShallowMergeSxProps(baseGridSx, autoSizeColumns ? AUTOSIZED_DATA_GRID_SX : undefined);

    const isAutosizePending = useAutosizeColumns({
        apiRef,
        enabled: autoSizeColumns && autosizeEnabled && (!(persistAutosize || persistColumnWidths) || preferencesLoaded),
        hasRenderedRows: !loading && Boolean(rows?.length),
        isEmptyAndSettled: !loading && !rows?.length,
        syncActionsPinning: autoSizeColumns && includeActions
    });

    // ============================================================================
    // ROW EDITING OPERATIONS
    // ============================================================================
    const getFirstEditableColumn = useCallback(() => {
        return find(columns, (c: GridColDef) => c.editable);
    }, [columns]);

    const canEditRow = useCallback(
        (row?: GridRowModel) => {
            if (!row) return false;
            if (row.isNew) return canCreateRow;

            if (entityName) {
                const { allowed, pending } = evaluateEntitySecurity(SECURITY_ACTIONS.EDIT, {
                    entityName,
                    entitySecurity: row.security
                });
                return Boolean(allowed && !pending);
            }

            // Legacy: callers without entityName.
            return canCreateRow && Boolean(getRowPermissions(row.security)[AllPermissions.Edit]);
        },
        [canCreateRow, entityName, evaluateEntitySecurity]
    );

    const resolvedIsCellEditable = useCallback(
        (params: GridCellParams) => (!isCellEditable || isCellEditable(params)) && canEditRow(params.row),
        [canEditRow, isCellEditable]
    );

    const startEdit = useCallback(
        (id: GridRowId, field: string, row, isNew: boolean = false) => {
            row.isNew = isNew;
            setActiveRow(row);
            apiRef.current.startRowEditMode({ id: id, fieldToFocus: field });
            onRowEditStart?.();
        },
        [apiRef, onRowEditStart]
    );

    const stopEdit = useCallback(
        (id: GridRowId, rollback?: boolean) => {
            setPinnedRows(undefined);

            if (!exitEditModeBeforeProcessRowUpdate) {
                apiRef.current.stopRowEditMode({ id: id, ignoreModifications: rollback });
                setActiveRow(undefined);
                if (rollback && activeRow) {
                    const rollbackRows = activeRow.isNew
                        ? rows.filter((row) => row.id !== id)
                        : rows.map((row) => (row.id === id ? activeRow : row));
                    setRows(rollbackRows);
                }
                onRowEditStop?.();
                return;
            }

            if (rollback) {
                apiRef.current.stopRowEditMode({ id: id, ignoreModifications: true });
                setActiveRow((current) => (current?.id === id ? undefined : current));

                const cancelledRow = getOriginalRowForEdit(id, activeRow, rowsRef.current);
                if (cancelledRow) {
                    const rollbackRows = cancelledRow.isNew
                        ? rowsRef.current.filter((row) => row.id !== id)
                        : rowsRef.current.map((row) => (row.id === id ? cancelledRow : row));
                    setRows(rollbackRows);
                }
                onRowEditStop?.();
                return;
            }

            // MUI keeps the row in edit mode (and greyed out) until an async `processRowUpdate`
            // resolves, which lets a second row look like it is being edited too. Leave edit mode
            // immediately and persist the committed values in the background instead.
            // Flush debounced cell values the same way MUI does before it commits a row.
            flushPendingRowEditMutations(apiRef.current, id);
            const rowUpdate = apiRef.current.getRowWithUpdatedValues(id, undefined);
            const originalRow = getOriginalRowForEdit(id, activeRow, rowsRef.current) ?? rowUpdate;

            apiRef.current.stopRowEditMode({ id: id, ignoreModifications: true });
            setActiveRow((current) => (current?.id === id ? undefined : current));
            onRowEditStop?.();

            void Promise.resolve(processRowUpdateCallbackRef.current?.(rowUpdate, originalRow)).catch((error) =>
                console.error('Row update error:', error)
            );
        },
        [activeRow, apiRef, exitEditModeBeforeProcessRowUpdate, onRowEditStop, rows, setRows]
    );

    const hasUnsavedRowChanges = useCallback(
        (id: GridRowId, field?: string) => {
            const originalRow = getOriginalRowForEdit(id, activeRow, rowsRef.current);
            if (!originalRow) {
                // Without an original snapshot, avoid treating focus-out as a clean cancel.
                return true;
            }

            const updatedRow = apiRef.current.getRowWithUpdatedValues(id, field);
            return hasEditableRowChanges(originalRow, updatedRow, columns);
        },
        [activeRow, apiRef, columns]
    );

    const handleEditClick = useCallback(
        (id: GridRowId) => {
            const editedRow = rows.find((row) => row.id === id);
            if (!canEditRow(editedRow)) {
                return;
            }

            const firstEditableColumn = find(columns, (c: GridColDef) => c.editable);
            startEdit(id, firstEditableColumn.field, editedRow);
        },
        [canEditRow, columns, rows, startEdit]
    );

    const handleEditCommit = useCallback(
        (id: GridRowId) => {
            if (getIsRowCommitInProgress?.(id)) {
                return false;
            }

            if (typeof isRowValid === 'function') {
                if (isRowValid(id)) {
                    stopEdit(id);
                    return true;
                }
                return false;
            }

            stopEdit(id);
            return true;
        },
        [getIsRowCommitInProgress, isRowValid, stopEdit]
    );

    const handleEditCancel = useCallback(
        (id: GridRowId) => {
            stopEdit(id, true);
            setPinnedRows(undefined);
        },
        [stopEdit]
    );

    const handleRowUpdate = useCallback(
        (update: GridRowModel, original: GridRowModel): GridRowModel => {
            const isNew = update.isNew;
            const updatedRow = { ...update, isNew: false };
            const hasExistingRow = rows.some((r) => r.id === update.id);

            const newRows: GridRowModel[] = hasExistingRow
                ? rows.map((r) => (r.id === update.id ? updatedRow : r))
                : isNew && newRowOnTop
                  ? [updatedRow, ...rows.filter((r) => r.id !== update.id)] // ← new row at TOP
                  : [...rows.filter((r) => r.id !== update.id), updatedRow];
            if (typeof handleRowAction === 'function') {
                handleRowAction(isNew ? RowAction.Add : RowAction.Edit, update, newRows);
            }
            setRows(newRows);
            return updatedRow;
        },
        [handleRowAction, newRowOnTop, rows, setRows]
    );

    const processRowUpdateCallback = useCallback(
        async (update: GridRowModel, original: GridRowModel) => {
            if (processRowUpdate) {
                // Call custom processRowUpdate and let it handle the update
                const result = await processRowUpdate(update, original);

                const isNewRow = !!update.isNew;
                const normalizedResult: GridRowModel = { ...result, isNew: false };

                if (setRows) {
                    const currentRows = rowsRef.current;
                    const resultRowIndex = currentRows.findIndex((r) => r.id === normalizedResult.id);
                    const draftRowIndex = currentRows.findIndex((r) => r.id === update.id);

                    if (resultRowIndex !== -1) {
                        const updatedRows = currentRows.map((row) =>
                            row.id === normalizedResult.id ? normalizedResult : row
                        );
                        setRows(updatedRows);
                    } else if (draftRowIndex !== -1) {
                        const updatedRows = currentRows.map((row) => (row.id === update.id ? normalizedResult : row));
                        setRows(updatedRows);
                    } else {
                        const updatedRows =
                            isNewRow && newRowOnTop
                                ? [normalizedResult, ...currentRows]
                                : [...currentRows, normalizedResult];
                        setRows(updatedRows);
                    }
                }

                if (typeof handleRowAction === 'function') {
                    const currentRows = rowsRef.current;
                    const hasExistingRow = currentRows.some(
                        (row) => row.id === normalizedResult.id || row.id === update.id
                    );
                    const action = isNewRow && !hasExistingRow ? RowAction.Add : RowAction.Edit;

                    let newRows: GridRowModel[];
                    if (isNewRow && newRowOnTop) {
                        newRows = [
                            normalizedResult,
                            ...currentRows.filter((row) => row.id !== update.id && row.id !== normalizedResult.id)
                        ];
                    } else if (isNewRow && !hasExistingRow) {
                        newRows = [
                            ...currentRows.filter((row) => row.id !== update.id && row.id !== normalizedResult.id),
                            normalizedResult
                        ];
                    } else {
                        newRows = currentRows.map((row) =>
                            row.id === update.id || row.id === normalizedResult.id ? normalizedResult : row
                        );
                    }

                    handleRowAction(action, normalizedResult, newRows);
                }

                return normalizedResult;
            }

            return handleRowUpdate(update, original);
        },
        [handleRowAction, handleRowUpdate, newRowOnTop, processRowUpdate, setRows]
    );

    processRowUpdateCallbackRef.current = processRowUpdateCallback;

    const onProcessRowUpdateErrorCallback = (err) => {
        console.error('Row update error:', err);
    };

    // ============================================================================
    // ROW DELETION
    // ============================================================================
    const handleDeleteClick = useCallback(
        (deleted: GridRowModel) => {
            if (typeof onDelete === 'function') {
                onDelete(deleted.id);
            } else {
                setRowPendingDeletion(deleted);
                setDeleteConfirmationOpen(true);
            }
        },
        [onDelete]
    );

    const handleRowChangeAfterDeletion = useCallback(
        (deletedRow: GridRowModel | undefined) => {
            if (!deletedRow) {
                return;
            }

            const newRows = rows.filter((r) => r.id !== deletedRow.id);
            setRows(newRows);

            if (typeof handleRowAction === 'function') {
                handleRowAction(RowAction.Delete, deletedRow, newRows);
            }
        },
        [handleRowAction, rows, setRows]
    );

    const handleDeleteConfirm = useCallback(() => {
        setDeleteConfirmationOpen(false);
        const deletedRow = rowPendingDeletion;

        if (typeof onDeleteHandle === 'function') {
            enableInlineDelete && handleRowChangeAfterDeletion(deletedRow);
            if (deletedRow) {
                onDeleteHandle(deletedRow);
            }
        } else {
            handleRowChangeAfterDeletion(deletedRow);
        }

        setRowPendingDeletion(undefined);
    }, [enableInlineDelete, handleRowChangeAfterDeletion, onDeleteHandle, rowPendingDeletion]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirmationOpen(false);
        setRowPendingDeletion(undefined);
    }, []);

    // ============================================================================
    // CELL RENDERING & STYLING
    // ============================================================================

    const getCellClassNameCallback = useCallback(
        (params: GridCellParams) => {
            const isInEditMode = !!activeRow;
            if (!isInEditMode) {
                const err = errors && errors[params.id] ? errors[params.id][params.field] : undefined;
                if (err) {
                    return 'MuiDataGrid-cell--errors';
                }
            }
            return undefined;
        },
        [activeRow, errors]
    );

    const getRowClassNameCallback = useCallback(
        (params) => (getRowClassName ? getRowClassName(params) : undefined),
        [getRowClassName]
    );

    // ============================================================================
    // COLUMNS CONFIGURATION
    // ============================================================================

    const dataColumns = useMemo(() => {
        const groupedFields = new Set(
            enableGridSettings ? (gridSettingsRowGroupingModel ?? EMPTY_GRID_GROUPING_MODEL) : rowGroupingModelState
        );

        const renderCell = (params: GridRenderCellParams) => {
            return (
                <CellContainer
                    params={params}
                    clickableColumn={clickableColumn}
                    isCellClickable={isCellClickable}
                    feature={feature}
                    entityName={entityName}
                    onDetails={onDetails}
                    onEdit={onEdit}
                >
                    <RenderDataGridCellValue params={params} tooltipOnOverflowOnly={noWrapColValue} />
                </CellContainer>
            );
        };

        const hasRows = Boolean(rows?.length);

        const transformedColumns: GridColDef[] = columns.map((column): GridColDef => {
            const sourceColumn = prepareColumnForAutosize(column, {
                enabled: autoSizeColumns,
                hasRows,
                hasContent: hasRows && columnHasAutosizeContent(column, rows ?? [])
            });
            let transformedColumn: GridColDef = asIsColumns?.includes(sourceColumn.field)
                ? sourceColumn
                : {
                      ...sourceColumn,
                      filterable: false,
                      renderCell
                  };

            if (enableRichTextEditing && isRichTextColumn(sourceColumn)) {
                const clampLines = sourceColumn.richTextClampLines ?? 1;
                transformedColumn = {
                    ...transformedColumn,
                    renderCell: (params: GridRenderCellParams) => {
                        const disabledTooltip =
                            typeof sourceColumn.richTextDisabledTooltip === 'function'
                                ? sourceColumn.richTextDisabledTooltip(params)
                                : sourceColumn.richTextDisabledTooltip;
                        const cellContent = (
                            <CellContainer
                                params={params}
                                clickableColumn={clickableColumn}
                                isCellClickable={isCellClickable}
                                feature={feature}
                                entityName={entityName}
                                onDetails={onDetails}
                                onEdit={onEdit}
                            >
                                <RichTextDisplayCell value={params.value} clampLines={clampLines} />
                            </CellContainer>
                        );

                        return hasTooltipContent(disabledTooltip) ? (
                            <Tooltip title={disabledTooltip} describeChild>
                                <span style={FULL_SIZE_TOOLTIP_ANCHOR_STYLE}>{cellContent}</span>
                            </Tooltip>
                        ) : (
                            cellContent
                        );
                    },
                    renderEditCell: (params) => (
                        <RichTextEditCell
                            {...params}
                            slashMacroActions={
                                typeof sourceColumn.richTextSlashMacroActions === 'function'
                                    ? sourceColumn.richTextSlashMacroActions(params)
                                    : sourceColumn.richTextSlashMacroActions
                            }
                            autoOpenSlashMacro={sourceColumn.richTextAutoOpenSlashMacro}
                            enableSlashMacroAutoOpenOnRootClick={
                                sourceColumn.richTextEnableSlashMacroAutoOpenOnRootClick
                            }
                            suppressAutoOpenSlashMacro={sourceColumn.richTextSuppressAutoOpenSlashMacro}
                        />
                    )
                };
            }

            if (enableGridSettings) {
                transformedColumn = withFlatSortComparator(
                    transformedColumn,
                    gridSettingsFlatSortField,
                    gridSettingsFlatSortComparator
                );
            }

            if (!groupedFields.has(sourceColumn.field)) {
                return transformedColumn;
            }

            const groupingValueGetter = transformedColumn.groupingValueGetter;

            return {
                ...transformedColumn,
                groupingValueGetter: (value, row, column, apiRef) => {
                    const groupingValue = groupingValueGetter ? groupingValueGetter(value, row, column, apiRef) : value;
                    return getGroupingLabelValue(groupingValue, transformedColumn, missingLabel);
                }
            };
        });

        if (includeActions) {
            const renderActionsCell = (params: GridRenderCellParams) => {
                const shouldIncludeActionsCell = ROW_TYPES_WITH_ACTIONS_MENU.includes(params.rowNode?.type);
                return shouldIncludeActionsCell ? <DataGridActionsCell {...params} /> : null;
            };

            const actionsColumn: GridColDef = {
                field: 'actions',
                type: 'actions',
                width: isMobile ? 50 : 180,
                hideable: false,
                resizable: false,
                disableReorder: true,
                renderCell: renderActionsCell
            };
            return [...transformedColumns, actionsColumn];
        }

        return transformedColumns;
    }, [
        asIsColumns,
        autoSizeColumns,
        clickableColumn,
        columns,
        enableGridSettings,
        gridSettingsFlatSortComparator,
        gridSettingsFlatSortField,
        gridSettingsRowGroupingModel,
        enableRichTextEditing,
        entityName,
        feature,
        includeActions,
        isCellClickable,
        isMobile,
        missingLabel,
        noWrapColValue,
        onDetails,
        onEdit,
        rowGroupingModelState,
        rows
    ]);

    const sizedColumns = useMemo(() => applyManualColumnWidths(dataColumns, manualWidths), [dataColumns, manualWidths]);

    const {
        columnVisibiltyModel,
        setColumnVisibiltyModel,
        selectedPresetLabel,
        ColumnVisibilitySelector,
        handleColumnOrderChange
    } = useDataGridColumnViewFeature({
        enabled: canSaveColumnView,
        trackColumnOrder: enableColumnOrder,
        trackColumnVisibility: enableSaveColumnView,
        gridId: id,
        apiRef,
        columns,
        rows,
        sizedColumns,
        onColumnOrderChange,
        presetsLabelToken: toolbar.presets?.labelToken,
        defaultColumnVisibility,
        columnVisibilityPresets,
        defaultSelectedPreset,
        enabledFavouritePreset,
        hideMyColumnsPreset,
        defaultSelectedPresetLabel,
        hideAllColumnsPreset,
        hideDefaultColumnsPreset,
        ...(enableGridSettings
            ? {
                  getCurrentGridSettings,
                  applyGridSettings,
                  defaultGridSettings,
                  gridSettingsRevision
              }
            : {})
    });

    const finalColumnVisibilityModel = useMemo(() => {
        if (!toolbar?.presets || toolbar.presets.visible === false) {
            if (externalColumnVisibilityModel) {
                return { ...stripFavouriteFields(externalColumnVisibilityModel), actions: true };
            }
            return { ...stripFavouriteFields(columnVisibiltyModel), actions: true };
        }
        return stripFavouriteFields(externalColumnVisibilityModel || columnVisibiltyModel);
    }, [externalColumnVisibilityModel, columnVisibiltyModel, toolbar?.presets]);

    useEffect(() => {
        if (enableGridSettings) {
            return;
        }

        if (selectedPresetLabel && groupForPreset.length > 0) {
            const selectedGroup = groupForPreset.find((group) => group.label === selectedPresetLabel);
            if (selectedGroup) {
                setRowGroupingModelState(selectedGroup.value);
            }
        }
    }, [enableGridSettings, groupForPreset, selectedPresetLabel]);

    const finalColumnVisibility = useMemo(
        () => (forceDefaultColumnVisibility ? defaultColumnVisibility : finalColumnVisibilityModel),
        [defaultColumnVisibility, finalColumnVisibilityModel, forceDefaultColumnVisibility]
    );

    // ============================================================================
    // TOOLBAR & NEW ROW CREATION
    // ============================================================================

    const createDefaultsFields = useMemo(() => columns.map((column) => column.field), [columns]);

    const applyCreateDefaults = useCreateDefaultsFromViewContext({
        enabled: Boolean(createDefaultsFromViewContext),
        fields: createDefaultsFields,
        facets: createDefaultsFromViewContext?.facets,
        fieldMetadata: createDefaultsFromViewContext?.fieldMetadata
    });

    const createNewRow = useCallback(() => {
        if (!canCreateRow) {
            return;
        }

        if (enableInlineEditing && !activeRow) {
            const maxNumericId = rows.reduce<number>((maxId, row) => {
                const numericId = Number(row.id);
                if (!Number.isFinite(numericId)) {
                    return maxId;
                }
                return numericId > maxId ? numericId : maxId;
            }, -1);
            const newId = maxNumericId + 1;

            const newRow: GridRowModel = { id: newId };
            columns.forEach((column) => {
                newRow[column.field] = '';
            });
            applyCreateDefaults(newRow);
            const hasGrouping = rowGroupingModelState.length > 0;
            const shouldUsePinnedDraftForGrouping = hasGrouping && newRowOnTop;

            if (newRowOnTop && !hasGrouping) {
                setPinnedRows({ top: [newRow] });
            }
            if (typeof onCreateNewRow === 'function') {
                onCreateNewRow(newRow);
            }
            if (shouldUsePinnedDraftForGrouping) {
                setPinnedRows({ top: [newRow] });
            } else {
                const newRows: GridRowModel[] = newRowOnTop ? [newRow, ...rows] : [...rows, newRow];
                setRows(newRows);
            }

            const firstEditableColumn = getFirstEditableColumn();
            if (firstEditableColumn) {
                setTimeout(() => {
                    const gridRow = apiRef.current?.getRow(newRow.id);
                    if (gridRow || shouldUsePinnedDraftForGrouping) {
                        startEdit(newRow.id, firstEditableColumn.field, newRow, true);
                    }
                }, 0);
            }
        } else if (typeof onAdd === 'function') {
            onAdd();
        }
    }, [
        canCreateRow,
        enableInlineEditing,
        activeRow,
        onAdd,
        rows,
        columns,
        newRowOnTop,
        onCreateNewRow,
        setRows,
        apiRef,
        applyCreateDefaults,
        getFirstEditableColumn,
        startEdit,
        rowGroupingModelState
    ]);

    const toolbarProps: ToolbarPropsOverrides = useMemo(
        () => ({
            id,
            toolbar,
            toolbarOptions,
            toolbarSx,
            isAddDisabled: !!activeRow || !!rowPendingDeletion || disableAdd || !canCreateRow,
            addTitle,
            createNewRow,
            ColumnVisibilitySelector,
            gridSettings: gridSettingsControl
        }),
        [
            activeRow,
            gridSettingsControl,
            rowPendingDeletion,
            addTitle,
            ColumnVisibilitySelector,
            createNewRow,
            canCreateRow,
            disableAdd,
            id,
            toolbar,
            toolbarOptions,
            toolbarSx
        ]
    );

    // ============================================================================
    // GRID STATE INITIALIZATION
    // ============================================================================
    const initialStateWithApiRef = useMemo(
        () => ({
            initialState: {
                columns: {
                    columnVisibilityModel: forceDefaultColumnVisibility
                        ? defaultColumnVisibility
                        : finalColumnVisibilityModel
                },
                pinnedColumns: autoSizeColumns ? { right: [] } : { right: ['actions'] },
                rowGrouping: {
                    model: rowGroupingModelState
                },
                sorting: {}
            },
            apiRef
        }),
        [
            apiRef,
            autoSizeColumns,
            finalColumnVisibilityModel,
            forceDefaultColumnVisibility,
            defaultColumnVisibility,
            rowGroupingModelState
        ]
    );

    const keepGroupedColumnsHidden = useKeepGroupedColumnsHidden(initialStateWithApiRef);

    const initialState = useMemo(
        () => (isGroupedColumnsHidden ? keepGroupedColumnsHidden : initialStateWithApiRef.initialState),
        [isGroupedColumnsHidden, keepGroupedColumnsHidden, initialStateWithApiRef]
    );

    // ============================================================================
    // EVENT HANDLERS - Row Editing
    // ============================================================================
    const handleRowEditStartCallback = useCallback(
        (params: GridRowEditStartParams, event: MuiEvent<SyntheticEvent>) => {
            event.defaultMuiPrevented = true;
            if (!activeRow && canEditRow(params.row)) {
                startEdit(params.id, params.field, params.row);
            }
        },
        [activeRow, canEditRow, startEdit]
    );

    const handleRowEditStopCallback = useCallback(
        (params: GridRowEditStopParams, event: MuiEvent<SyntheticEvent>) => {
            event.defaultMuiPrevented = true;

            const { reason, id } = params;

            if (reason === DATA_GRID_EVENTS.escapeKeyDown) {
                handleEditCancel(id);
                return;
            }

            if (reason === DATA_GRID_EVENTS.enterKeyDown) {
                handleEditCommit(id);
                return;
            }

            if (reason === DATA_GRID_EVENTS.rowFocusOut) {
                if (commitRowOnBlur) {
                    const focusOutAction = getRowFocusOutEditAction({
                        commitOnlyWhenDirty: commitRowOnBlurOnlyWhenDirty,
                        hasUnsavedChanges: hasUnsavedRowChanges(id, params.field),
                        isCommitInProgress: getIsRowCommitInProgress?.(id) ?? false,
                        shouldSuppressCommit: getShouldSuppressRowCommitOnBlur?.(params, event) ?? false
                    });

                    if (focusOutAction === ROW_FOCUS_OUT_EDIT_ACTIONS.suppress) {
                        return;
                    }

                    if (focusOutAction === ROW_FOCUS_OUT_EDIT_ACTIONS.cancelClean) {
                        handleEditCancel(id);
                        return;
                    }

                    handleEditCommit(id);
                }
                if (inActivateOnBlur) {
                    setActiveRow(undefined);
                }
                return;
            }
        },
        [
            commitRowOnBlur,
            commitRowOnBlurOnlyWhenDirty,
            handleEditCancel,
            handleEditCommit,
            hasUnsavedRowChanges,
            inActivateOnBlur,
            getIsRowCommitInProgress,
            getShouldSuppressRowCommitOnBlur
        ]
    );

    // ============================================================================
    // EVENT HANDLERS - Pagination
    // ============================================================================
    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            if (model.page !== page) {
                onPageChange(model.page);
            }
            if (model.pageSize !== pageSize) {
                onPageSizeChange(model.pageSize);
            }
        },
        [onPageChange, onPageSizeChange, page, pageSize]
    );

    // ============================================================================
    // EVENT HANDLERS - Columns
    // ============================================================================
    const handleColumnVisibilityModelChange = useCallback(
        (newModel) => {
            if (typeof externalOnColumnVisibilityModelChange === 'function') {
                externalOnColumnVisibilityModelChange(newModel);
            } else {
                if (isFirstPresetChange.current) {
                    isFirstPresetChange.current = false;

                    if (Object.values(newModel).length === 1 && Object.values(newModel)[0] === false) {
                        return;
                    }
                }
                if (!forceDefaultColumnVisibility) {
                    setColumnVisibiltyModel(newModel);
                }
            }
        },
        [externalOnColumnVisibilityModelChange, forceDefaultColumnVisibility, setColumnVisibiltyModel]
    );

    const handleRowGroupingModelChange = useCallback(
        (newModel) => {
            if (enableGridSettings) {
                return;
            }

            setRowGroupingModelState(newModel);
            onRowGroupingModelChange?.(newModel);
        },
        [enableGridSettings, onRowGroupingModelChange]
    );

    // ============================================================================
    // AGGREGATION & SLOTS CONFIGURATION
    // ============================================================================
    const aggregationFunctions = useMemo(
        () => ({
            ...GRID_AGGREGATION_FUNCTIONS,
            ...(customAggregationFunctions
                ? Object.fromEntries(
                      Object.entries(customAggregationFunctions).map(([key, fn]) => [
                          key,
                          {
                              ...fn,
                              label: typeof fn.label === 'string' ? <I18n token={fn.label} /> : fn.label
                          }
                      ])
                  )
                : {})
        }),
        [customAggregationFunctions]
    );

    const slotsObject = useMemo(
        () => ({
            toolbar: CustomToolbar,
            loadingOverlay: CustomLoadingOverlay,
            ...slots
        }),
        [slots]
    );

    const slotPropsObject = useMemo(
        () => ({
            columnsPanel: {
                disableHideAllButton: true,
                disableShowAllButton: true
            },
            toolbar: toolbarProps as ToolbarPropsOverrides,
            ...slotProps
        }),
        [slotProps, toolbarProps]
    );

    const resolvedGroupingColDef = useMemo(() => {
        const existingCellClassName = groupingColDef?.cellClassName;

        return {
            ...groupingColDef,
            ...(gridSettingsGroupingSortComparator ? { sortComparator: gridSettingsGroupingSortComparator } : {}),
            cellClassName: (params: GridCellParams) => {
                const inheritedClassName =
                    typeof existingCellClassName === 'function' ? existingCellClassName(params) : existingCellClassName;

                const isGroupRow = params.rowNode?.type === 'group';
                const missingGroupingLabelClass =
                    isGroupRow && isMissingGroupingLabel(params.formattedValue, missingLabel)
                        ? 'missingGroupingLabel'
                        : '';

                return [inheritedClassName, missingGroupingLabelClass].filter(Boolean).join(' ');
            }
        };
    }, [gridSettingsGroupingSortComparator, groupingColDef, missingLabel]);

    // ============================================================================
    // CUSTOM ACTIONS & CONTEXT
    // ============================================================================
    const transformedCustomActions = useMemo(
        () => transformDataGridCustomActions(customActions, () => apiRef.current.getAllColumns()),
        [customActions, apiRef]
    );

    const actionsContextValue = useMemo<DataGridActionsContextType>(
        () => ({
            apiRef,
            entityName,
            feature,
            activeRow,
            isRowValid,
            onEdit,
            onShare,
            enableInlineEditing,
            enableInlineDelete,
            onDelete,
            onDeleteHandle,
            copyTooltipTitle,
            onCopy,
            onActivateOrInactivateHandle,
            customActions: transformedCustomActions,
            customRowActions,
            isMobile,
            actionLabels,
            handleEditClick,
            handleEditCommit,
            handleEditCancel,
            handleDeleteClick,
            hideEditActionWhenInlineEditing
        }),
        [
            apiRef,
            entityName,
            feature,
            activeRow,
            isRowValid,
            onEdit,
            onShare,
            enableInlineEditing,
            enableInlineDelete,
            onDelete,
            onDeleteHandle,
            copyTooltipTitle,
            onCopy,
            onActivateOrInactivateHandle,
            transformedCustomActions,
            customRowActions,
            isMobile,
            actionLabels,
            handleEditClick,
            handleEditCommit,
            handleEditCancel,
            handleDeleteClick,
            hideEditActionWhenInlineEditing
        ]
    );

    // ============================================================================
    // ROW HEIGHT CALCULATION
    // ============================================================================
    const getRowHeight = useCallback(() => 'auto', []);

    const getRowSpacing = useCallback((params: { isFirstVisible: boolean }) => {
        return {
            top: params.isFirstVisible ? 12 : 6,
            bottom: 6
        };
    }, []);

    // ============================================================================
    // CELL KEY DOWN EVENT HANDLER
    // ============================================================================
    const handleCellKeyDownEvent = useCallback(
        (_: GridCellParams, event: MuiEvent<KeyboardEvent>) => {
            const target = event.target;

            let isEditableTarget = false;
            if (target instanceof HTMLElement) {
                isEditableTarget =
                    target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
            }

            if (listViewConfiguration.enableListView && isEditableTarget) {
                event.defaultMuiPrevented = true;
            }
        },
        [listViewConfiguration.enableListView]
    );

    useEffect(() => {
        if (
            externalAddTriggerValue !== undefined &&
            externalAddTriggerValue !== null &&
            externalAddTriggerValue > 0 &&
            externalAddTriggerValue !== externalAddTriggerValueHandledRef.current &&
            !disableAdd
        ) {
            externalAddTriggerValueHandledRef.current = externalAddTriggerValue;
            createNewRow();
        }
    }, [externalAddTriggerValue, disableAdd, createNewRow]);

    return (
        <>
            <DataGridActionsContext.Provider value={actionsContextValue}>
                <DataGridPremium
                    apiRef={apiRef}
                    // DataGrid configuration
                    rows={rows}
                    columns={sizedColumns}
                    loading={loading || Boolean(isAutosizePending)}
                    autoHeight={autoHeight}
                    disableVirtualization={disableVirtualization}
                    density={density}
                    initialState={initialState}
                    hideFooter={hideFooter}
                    experimentalFeatures={EXPERIMENTAL_FEATURES}
                    getRowHeight={getRowHeight}
                    getRowSpacing={listViewConfiguration.enableListView ? getRowSpacing : undefined}
                    rowBufferPx={listViewConfiguration.enableListView ? listViewConfiguration.rowBufferPx : undefined}
                    // Pagination configuration
                    pagination={!disablePagination}
                    hideFooterRowCount={hideFooterRowCount}
                    paginationModel={paginationModel}
                    paginationMode={paginationMode}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={DATA_GRID_CONSTANTS.rowsPerPageOptions}
                    // Sorting configuration
                    sortingMode={sortingMode}
                    sortModel={enableGridSettings ? gridSettingsSortModel : sortModel}
                    filterModel={filterModel}
                    onSortModelChange={enableGridSettings ? onGridSettingsSortModelChange : onSortModelChange}
                    // Selection configuration
                    checkboxSelection={checkboxSelection}
                    // Grouping configuration
                    defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
                    // Rows handlers
                    rowCount={serverPagination ? rowCount : undefined}
                    pinnedRows={pinnedRows}
                    rowGroupingModel={
                        enableGridSettings
                            ? (gridSettingsRowGroupingModel ?? EMPTY_GRID_GROUPING_MODEL)
                            : rowGroupingModelState
                    }
                    onRowGroupingModelChange={handleRowGroupingModelChange}
                    disableRowSelectionOnClick={disableRowSelectionOnClick}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    isRowSelectable={isRowSelectable}
                    detailPanelExpandedRowIds={detailPanelExpandedRowIdsSet}
                    onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                    rowReordering={rowReordering}
                    onRowOrderChange={onRowOrderChange}
                    hideFooterSelectedRowCount={hideFooterSelectedRowCount}
                    getRowClassName={getRowClassNameCallback}
                    // Columns handlers
                    columnVisibilityModel={finalColumnVisibility}
                    pinnedColumnsSectionSeparator={null}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    disableColumnSorting={disableColumnSorting || enableGridSettings}
                    columnHeaderHeight={columnHeaderHeight}
                    disableColumnResize={disableColumnResize}
                    disableColumnFilter={disableColumnFilter}
                    showColumnVerticalBorder={false}
                    onColumnOrderChange={handleColumnOrderChange}
                    disableColumnMenu={disableColumnMenu}
                    // Inline row editing handlers
                    editMode={editMode}
                    isCellEditable={enableInlineEditing ? resolvedIsCellEditable : isCellEditable}
                    onRowEditStart={handleRowEditStartCallback}
                    onRowEditStop={handleRowEditStopCallback}
                    processRowUpdate={processRowUpdateCallback}
                    onProcessRowUpdateError={onProcessRowUpdateErrorCallback}
                    onCellKeyDown={handleCellKeyDownEvent}
                    // Detail panel configuration
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    // ListView
                    listView={listViewConfiguration.enableListView}
                    listViewColumn={listViewConfiguration.listViewColDef}
                    // Aggregation
                    aggregationModel={aggregationModel}
                    aggregationFunctions={aggregationFunctions}
                    // Slots
                    slots={slotsObject}
                    slotProps={slotPropsObject}
                    showToolbar={showToolbar}
                    // Styling
                    className={
                        isAutosizePending ? `${classes.container} ${AUTOSIZE_PENDING_CLASSNAME}` : classes.container
                    }
                    sx={finalCustomSx}
                    getCellClassName={getCellClassNameCallback}
                    groupingColDef={resolvedGroupingColDef}
                    autosizeOptions={autoSizeColumns ? AUTOSIZE_OPTIONS : undefined}
                />
            </DataGridActionsContext.Provider>
            {gridSettingsModal}
            <Confirm
                open={deleteConfirmationOpen}
                title={<I18n token={'delete.confirm.title'} />}
                okLabelI18n="delete.confirm.title"
                body={
                    <Typography>
                        <I18n token={'delete.confirm.message'} />
                    </Typography>
                }
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </>
    );
}
